import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["ariaLabel", "button", "className", "fwdRef", "lessId", "messages", "moreId", "onLess", "onMore", "showLess", "showMore"],
  _excluded2 = ["allowOverflowWhenOpen", "alwaysVisible", "aria-label", "button", "children", "contentId", "eagerRender", "fwdRef", "onChange", "step"];
import React, { Children, cloneElement, forwardRef, isValidElement, useState, useImperativeHandle, useRef } from 'react';
import SmoothCollapse from 'react-smooth-collapse';
import { v4 as uuidv4 } from 'uuid';
import { IconCollapse, IconExpand } from '@op/opds.opux-icons/navigation';
import { Action, Button } from '@op/opds.opux';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import { useUpdateEffect } from '../common/hooks';
import { useLocale } from '../common/locale';
export const ANIMATION_DURATION_MS = 250;
const messages = {
  en: {
    less: 'Show less',
    more: 'Show more'
  },
  fi: {
    less: 'Näytä vähemmän',
    more: 'Näytä enemmän'
  },
  sv: {
    less: 'Visa mindre',
    more: 'Visa mer'
  }
};
const MoreButton = _ref => {
  let {
    ariaLabel,
    more,
    moreId,
    onMore
  } = _ref;
  return /*#__PURE__*/React.createElement(Button, {
    "aria-controls": moreId,
    "aria-label": ariaLabel,
    "data-more-control-identifier": true,
    compact: true,
    icon: IconExpand,
    iconPos: "right",
    onClick: onMore,
    type: "button"
  }, more);
};
const LessButton = _ref2 => {
  let {
    ariaLabel,
    less,
    lessId,
    onLess
  } = _ref2;
  return /*#__PURE__*/React.createElement(Button, {
    "aria-controls": lessId,
    "aria-label": ariaLabel,
    "data-less-control-identifier": true,
    compact: true,
    iconPos: "right",
    type: "button",
    icon: IconCollapse,
    onClick: onLess
  }, less);
};
const MoreAction = _ref3 => {
  let {
    ariaLabel,
    handleKeyDown,
    more,
    moreId,
    onMore
  } = _ref3;
  return /*#__PURE__*/React.createElement(Action, {
    "aria-controls": moreId,
    "aria-label": ariaLabel,
    "data-more-control-identifier": true,
    icon: IconExpand,
    onClick: onMore,
    size: "small",
    onKeyDown: event => handleKeyDown(event, onMore)
  }, more);
};
const LessAction = _ref4 => {
  let {
    ariaLabel,
    handleKeyDown,
    less,
    lessId,
    onLess
  } = _ref4;
  return /*#__PURE__*/React.createElement(Action, {
    "aria-controls": lessId,
    "aria-label": ariaLabel,
    "data-less-control-identifier": true,
    icon: IconCollapse,
    onClick: onLess,
    size: "small",
    onKeyDown: event => handleKeyDown(event, onLess)
  }, less);
};
const MoreLessControls = _ref5 => {
  let {
      ariaLabel,
      button,
      className,
      fwdRef,
      lessId,
      messages: propMessages,
      moreId,
      onLess,
      onMore,
      showLess,
      showMore
    } = _ref5,
    props = _objectWithoutPropertiesLoose(_ref5, _excluded);
  const lc = useLocale();
  const {
    less,
    more
  } = propMessages || messages[lc];
  const cn = buildVersionedClassNames('ds-more-less', className);
  function handleKeyDown(event, action) {
    if (event.key !== 'Tab' && (event.key === ' ' || event.key === 'Enter')) {
      event.preventDefault();
      action();
    }
  }
  function renderMore() {
    if (showMore && button) {
      return /*#__PURE__*/React.createElement(MoreButton, {
        ariaLabel: ariaLabel == null ? void 0 : ariaLabel.moreControlLabel,
        more: more,
        moreId: moreId,
        onMore: onMore
      });
    }
    if (showMore) {
      return /*#__PURE__*/React.createElement(MoreAction, {
        ariaLabel: ariaLabel == null ? void 0 : ariaLabel.moreControlLabel,
        handleKeyDown: handleKeyDown,
        more: more,
        moreId: moreId,
        onMore: onMore
      });
    }
    return null;
  }
  function renderLess() {
    if (showLess && button) {
      return /*#__PURE__*/React.createElement(LessButton, {
        ariaLabel: ariaLabel == null ? void 0 : ariaLabel.lessControlLabel,
        less: less,
        lessId: lessId,
        onLess: onLess
      });
    }
    if (showLess) {
      return /*#__PURE__*/React.createElement(LessAction, {
        ariaLabel: ariaLabel == null ? void 0 : ariaLabel.lessControlLabel,
        handleKeyDown: handleKeyDown,
        less: less,
        lessId: lessId,
        onLess: onLess
      });
    }
    return null;
  }
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cn,
    ref: fwdRef
  }, props), renderMore(), renderLess());
};

/**
 * _A component to reveal and hide additional relatively simple content above its control(s)._
 *
 * ***
 *
 * Allows revealing and hiding additional content with localized "Show more"
 * and "Show less" [`<Action>`](#/Actions/Action) or compact [`<Button>`](#/Actions/Button)
 * controls. One or more children will be revealed or hidden in stages.
 *
 * Exposes `open()` and `close()` imperative methods. Both take in one boolean
 * parameter. If this parameter is set, calling these methods opens or closes
 * all children.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping `<div>` element.
 */

export const MoreLess = _ref6 => {
  let {
      allowOverflowWhenOpen = false,
      alwaysVisible = 0,
      'aria-label': ariaLabel,
      button = false,
      children,
      contentId: propContentId,
      eagerRender = false,
      fwdRef,
      onChange,
      step = 0
    } = _ref6,
    props = _objectWithoutPropertiesLoose(_ref6, _excluded2);
  const contentRef = useRef(null);
  const controlRef = useRef(null);
  const [openNumber, setOpenNumber] = useState(0);
  const [stateContentId] = useState(() => "ds-ml-" + uuidv4());
  const contentId = propContentId != null ? propContentId : stateContentId;
  const childrenArray = Array.isArray(children) ? children : [children];
  const ctrlRefCurrent = controlRef.current;

  // Will leave the array as an array of anything because the children can contain
  // pretty much anything.
  const chunk = (arr, size) => arr.reduce((groups, item, i) => i % size === 0 ? [...groups, [item]] : [...groups.slice(0, -1), [...groups[groups.length - 1], item]], []);
  const chunkedChildren = chunk(Children.toArray(childrenArray.slice(alwaysVisible)), step < 1 ? Children.count(childrenArray) : step);
  const getChunkId = index => step === 0 ? contentId : contentId + "-" + index;
  function handleLess(all) {
    setOpenNumber(all ? 0 : Math.max(openNumber - 1, 0));
  }
  function handleMore(all) {
    setOpenNumber(all ? chunkedChildren.length : Math.min(openNumber + 1, chunkedChildren.length));
  }
  function handleOnChangeEnd(index) {
    const moreControlElement = ctrlRefCurrent == null ? void 0 : ctrlRefCurrent.firstChild;
    const focusMoreControl = openNumber === 0 && (moreControlElement == null ? void 0 : moreControlElement.hasAttribute('data-more-control-identifier'));

    // Focus the show more control if all children are hidden AND the first visible control is show more
    if (focusMoreControl) {
      moreControlElement.focus();
      return;
    }

    // Focus the revealed content
    if (openNumber !== index) {
      var _contentRef$current;
      (_contentRef$current = contentRef.current) == null || (_contentRef$current = _contentRef$current.childNodes[alwaysVisible + index]) == null || _contentRef$current.focus();
      return;
    }

    // Focus the show less control
    const lessControlElement = ctrlRefCurrent == null ? void 0 : ctrlRefCurrent.lastChild;
    if (lessControlElement.hasAttribute('data-less-control-identifier')) {
      lessControlElement.focus();
    }
  }
  useImperativeHandle(fwdRef, function () {
    return {
      close: handleLess,
      contentRef,
      controlRef,
      open: handleMore
    };
  });
  useUpdateEffect(() => {
    // To get screen readers to announce that the focus is still on the show less control when content is hidden in steps,
    // the focus first needs to be removed and then added again in onChangeEnd of the rc-slider
    if (step > 0) {
      const firstChild = ctrlRefCurrent == null ? void 0 : ctrlRefCurrent.firstChild;
      const lastChild = ctrlRefCurrent == null ? void 0 : ctrlRefCurrent.lastChild;
      if (lastChild.hasAttribute('data-less-control-identifier') && !firstChild.hasAttribute('data-less-control-identifier')) {
        lastChild.blur();
      }
    }
    if (onChange) {
      const visibleChildren = Math.min(alwaysVisible, Children.count(childrenArray)) + chunkedChildren.slice(0, openNumber).reduce((length, current) => length + current.length, 0);
      onChange(visibleChildren);
    }
  }, [openNumber]);
  return childrenArray.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: buildVersionedClass('ds-more-less__content'),
    ref: contentRef
  }, alwaysVisible > 0 && Children.toArray(childrenArray.slice(0, alwaysVisible)).map(child => {
    if (/*#__PURE__*/isValidElement(child)) {
      return /*#__PURE__*/cloneElement(child, {
        key: child.key
      });
    }
  }), chunkedChildren.map((child, i) => {
    const chunkId = getChunkId(i);
    return /*#__PURE__*/React.createElement(SmoothCollapse, {
      allowOverflowWhenOpen: allowOverflowWhenOpen,
      className: buildVersionedClassNames('ds-more-less__content-step', {
        'ds-more-less__content-step--closed': eagerRender && openNumber <= i
      }),
      eagerRender: eagerRender,
      expanded: openNumber > i,
      heightTransition: ANIMATION_DURATION_MS + "ms ease",
      id: chunkId,
      key: chunkId,
      onChangeEnd: () => handleOnChangeEnd(i),
      tabIndex: -1
    }, child);
  })), /*#__PURE__*/React.createElement(MoreLessControls, _extends({}, props, {
    ariaLabel: ariaLabel,
    button: button,
    fwdRef: controlRef,
    lessId: getChunkId(openNumber - 1),
    moreId: getChunkId(openNumber),
    onLess: () => handleLess(),
    onMore: () => handleMore(),
    showLess: openNumber > 0,
    showMore: openNumber < chunkedChildren.length
  }))) : null;
};
const MoreLessWithForwardRef = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(MoreLess, _extends({}, props, {
  fwdRef: ref
})));
MoreLessWithForwardRef.displayName = 'MoreLess';
export default MoreLessWithForwardRef;